/**
 * Phase 4D — Maintenance-first owner dashboard.
 *
 * Layout (mobile-first → 12-col grid on lg):
 *   Row 1: 4 count tiles (Aircraft / Overdue / Due 30d / Missing docs)
 *   Row 2: Fleet status (left, 8 cols) · Subscription tile (right, 4 cols)
 *   Row 3: Next due (left, 7 cols) · Upcoming renewals (right, 5 cols)
 *   Row 4: Parts due soon (left, 6 cols) · AD review (right, 6 cols)
 *   Row 5: Missing documents (left, 6 cols) · Recent flights (right, 6 cols)
 *
 * Everything talks to `/api/dashboard/owner` — one round-trip per page load.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AirplaneTilt, Wrench, BellRinging, ShieldCheck, FileText, ChartBar,
  CheckCircle, Warning, ArrowRight, CalendarBlank, Sparkle, Gear, Lightning,
  ClipboardText,
} from "@phosphor-icons/react";
import { api, fmtDate } from "../lib/api";
import { Surface, PageHeader, Btn } from "../components/Primitives";

// ─────────────────────────────────────────────────────────────────────────────
// Severity → colour helpers
// ─────────────────────────────────────────────────────────────────────────────
const SEV_COLOUR = {
  expired:    { dot: "bg-red-500",     text: "text-red-300",     border: "border-red-500/40",   bg: "bg-red-500/5",     label: "OVERDUE" },
  urgent_7d:  { dot: "bg-amber",       text: "text-amber",       border: "border-amber/50",     bg: "bg-amber/5",       label: "URGENT" },
  warning_30d:{ dot: "bg-yellow-500",  text: "text-yellow-300",  border: "border-yellow-500/40",bg: "bg-yellow-500/5",  label: "WARNING" },
};

const KIND_LABEL = {
  part: "Part",
  maintenance: "Maintenance",
  ad: "AD",
};

const KIND_ICON = {
  part: Gear,
  maintenance: Wrench,
  ad: ShieldCheck,
};

const DOC_KIND_LABEL = {
  airworthiness:    "Airworthiness certificate",
  registration:     "Aircraft registration",
  insurance:        "Insurance policy",
  weight_balance:   "Weight & balance",
  arc:              "ARC (Airworthiness Review)",
};

// ─────────────────────────────────────────────────────────────────────────────
// Building blocks
// ─────────────────────────────────────────────────────────────────────────────
function CountTile({ icon: Icon, label, value, hint, accent = "amber", onClick, testId }) {
  const accentMap = {
    amber:  "border-amber/40 hover:border-amber",
    red:    "border-red-500/40 hover:border-red-500",
    yellow: "border-yellow-500/40 hover:border-yellow-500",
    zinc:   "border-zinc-800 hover:border-zinc-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-5 border ${accentMap[accent]} bg-zinc-950/40 transition-colors group`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon size={18} className={accent === "red" ? "text-red-400" : accent === "yellow" ? "text-yellow-400" : "text-amber"} />
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">{label}</div>
      </div>
      <div className="text-3xl font-semibold tracking-tight">{value}</div>
      {hint && <div className="mt-2 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">{hint}</div>}
    </button>
  );
}

function SeverityBadge({ severity }) {
  const cfg = SEV_COLOUR[severity];
  if (!cfg) return null;
  return (
    <span className={`tag font-mono text-[9px] tracking-wider px-1.5 py-0.5 border ${cfg.border} ${cfg.text} ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
}

function ItemRow({ item, onClick }) {
  const KindIcon = KIND_ICON[item.kind] || Wrench;
  const cfg = SEV_COLOUR[item.severity];
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full grid grid-cols-12 items-center gap-3 px-3 py-2.5 border-l-2 border-zinc-800 hover:border-amber hover:bg-zinc-900/40 transition-colors text-left"
      data-testid={`item-${item.kind}-${item.id}`}
    >
      <div className="col-span-7 flex items-center gap-2 min-w-0">
        <KindIcon size={14} className={cfg?.text || "text-zinc-500"} weight="regular" />
        <div className="min-w-0">
          <div className="text-sm text-zinc-100 truncate">{item.name || "—"}</div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">
            {item.aircraft_registration} · {KIND_LABEL[item.kind] || item.kind}
            {item.category ? ` · ${item.category}` : ""}
          </div>
        </div>
      </div>
      <div className="col-span-3 text-right">
        <SeverityBadge severity={item.severity} />
      </div>
      <div className="col-span-2 text-right text-xs font-mono text-zinc-300 truncate">{item.due_label}</div>
    </button>
  );
}

function SectionCard({ title, hint, action, children, testId, count = null }) {
  return (
    <Surface className="p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-medium tracking-wide text-zinc-100 uppercase">{title}</h3>
            {count !== null && (
              <span className="text-[10px] font-mono text-zinc-500">· {count}</span>
            )}
          </div>
          {hint && <div className="text-xs text-zinc-500 mt-0.5">{hint}</div>}
        </div>
        {action}
      </div>
      {children}
    </Surface>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="py-8 text-center text-xs text-zinc-500">
      <Icon size={20} className="inline-block mb-2 text-zinc-700" />
      <div>{text}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subscription tile
// ─────────────────────────────────────────────────────────────────────────────
function SubscriptionTile({ sub, onClick }) {
  if (!sub) return null;
  const isTrial = sub.subscription_status === "trialing";
  const urgent = isTrial && (sub.trial_days_left ?? 99) <= 14;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left w-full p-5 border ${urgent ? "border-amber" : "border-zinc-800 hover:border-zinc-700"} bg-zinc-950/40 transition-colors group h-full`}
      data-testid="dashboard-subscription-tile"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={18} className={urgent ? "text-amber" : "text-zinc-400"} />
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Subscription</div>
      </div>
      <div className="text-2xl font-semibold tracking-tight" data-testid="dashboard-plan-label">
        {sub.plan_label || sub.plan}
      </div>
      <div className="mt-1 text-xs font-mono text-zinc-500 uppercase tracking-wider">
        {(sub.billing_cadence || "—")} · {(sub.subscription_status || "—").toUpperCase()}
      </div>
      {isTrial && (
        <div className={`mt-3 pt-3 border-t ${urgent ? "border-amber/30" : "border-zinc-800"}`}>
          <div className={`text-sm font-medium ${urgent ? "text-amber" : "text-zinc-200"}`} data-testid="dashboard-trial-days-left">
            {sub.trial_days_left === 0 ? "Trial ends today" : `${sub.trial_days_left} days left in trial`}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mt-1">
            Ends {fmtDate(sub.trial_ends_at)}
          </div>
        </div>
      )}
      {sub.period_total_eur != null && !isTrial && (
        <div className="mt-3 pt-3 border-t border-zinc-800 text-xs text-zinc-400">
          €{sub.period_total_eur}/{sub.billing_cadence === "annual" ? "yr" : "mo"} per aircraft
        </div>
      )}
      <div className="mt-3 text-[10px] font-mono text-amber group-hover:underline uppercase tracking-wider">
        Manage subscription <ArrowRight size={10} className="inline" />
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fleet status table
// ─────────────────────────────────────────────────────────────────────────────
function FleetStatus({ fleet, onClickAircraft }) {
  if (!fleet?.length) {
    return (
      <SectionCard title="Fleet" testId="dashboard-fleet-card">
        <EmptyState icon={AirplaneTilt} text="No aircraft yet — add your first in Fleet." />
      </SectionCard>
    );
  }
  return (
    <SectionCard
      title="Fleet status"
      hint="Per-aircraft maintenance health"
      count={fleet.length}
      testId="dashboard-fleet-card"
    >
      <div className="space-y-2">
        {fleet.map((a) => {
          const statusCfg = {
            overdue:  { dot: "bg-red-500",    text: "text-red-300",    label: "OVERDUE" },
            urgent:   { dot: "bg-amber",      text: "text-amber",      label: "URGENT" },
            warning:  { dot: "bg-yellow-500", text: "text-yellow-300", label: "WARNING" },
            ok:       { dot: "bg-emerald-500",text: "text-emerald-300",label: "OK" },
          }[a.status] || { dot: "bg-zinc-600", text: "text-zinc-400", label: "—" };
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onClickAircraft(a.id)}
              className="w-full flex items-center gap-4 px-3 py-3 border border-zinc-800 hover:border-amber/50 hover:bg-zinc-900/30 transition-colors text-left"
              data-testid={`fleet-row-${a.id}`}
            >
              <span className={`w-2 h-2 rounded-full ${statusCfg.dot} shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-100 truncate">
                  {a.registration} <span className="text-zinc-500 font-normal">· {a.manufacturer} {a.model}</span>
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  {a.tach_time != null ? `${Number(a.tach_time).toFixed(1)} h` : "—"}
                  {a.year_of_manufacture ? ` · ${a.year_of_manufacture}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider">
                {a.counts.overdue > 0 && <span className="text-red-300">{a.counts.overdue} overdue</span>}
                {a.counts.urgent > 0 && <span className="text-amber">{a.counts.urgent} urgent</span>}
                {a.counts.warning > 0 && <span className="text-yellow-300">{a.counts.warning} warning</span>}
                {a.counts.missing_docs > 0 && <span className="text-zinc-400">{a.counts.missing_docs} missing docs</span>}
                <span className={statusCfg.text}>{statusCfg.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/owner");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const goToAircraftItem = (item) => {
    if (!item.aircraft_id) return;
    const tabMap = { part: "Parts", maintenance: "Maintenance", ad: "Airworthiness" };
    navigate(`/fleet/${item.aircraft_id}`);
  };

  if (loading) {
    return (
      <div className="container py-10">
        <div className="text-zinc-500 font-mono text-xs uppercase tracking-[0.3em]">Loading dashboard…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="container py-10">
        <Surface className="p-6 border-red-500/40 bg-red-500/5">
          <Warning className="inline text-red-400 mr-2" />
          <span className="text-red-200">{err}</span>
        </Surface>
      </div>
    );
  }
  if (!data) return null;
  const { counts, fleet, next_due: nextDue, overdue, upcoming_renewals: upcoming,
          parts_due_soon: partsDue, ad_review: adReview, missing_documents: missing,
          recent_flights: flights, subscription } = data;

  return (
    <div className="container py-8 space-y-6" data-testid="dashboard-page">
      <PageHeader
        overline="OwnerCAM"
        title="Maintenance dashboard"
        subtitle="Know what's due, what's coming, and what's missing across your aircraft."
      />

      {/* ── Count tiles ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-count-tiles">
        <CountTile
          icon={AirplaneTilt} label="Aircraft" value={counts.aircraft}
          hint={counts.aircraft === 1 ? "in your fleet" : "in your fleet"}
          accent="amber"
          onClick={() => navigate("/fleet")}
          testId="count-tile-aircraft"
        />
        <CountTile
          icon={Warning} label="Overdue" value={counts.overdue}
          hint={counts.overdue > 0 ? "Action needed" : "Nothing overdue"}
          accent={counts.overdue > 0 ? "red" : "zinc"}
          onClick={() => navigate("/maintenance")}
          testId="count-tile-overdue"
        />
        <CountTile
          icon={BellRinging} label="Due in 30 days" value={counts.due_30d}
          hint="Upcoming maintenance + AD"
          accent={counts.due_30d > 0 ? "yellow" : "zinc"}
          onClick={() => navigate("/maintenance")}
          testId="count-tile-due-30"
        />
        <CountTile
          icon={FileText} label="Missing documents" value={counts.missing_docs}
          hint="Required document kinds"
          accent={counts.missing_docs > 0 ? "amber" : "zinc"}
          onClick={() => navigate("/documents")}
          testId="count-tile-missing-docs"
        />
      </div>

      {/* ── Row 2: Fleet + Subscription ── */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8">
          <FleetStatus fleet={fleet} onClickAircraft={(id) => navigate(`/fleet/${id}`)} />
        </div>
        <div className="lg:col-span-4">
          <SubscriptionTile sub={subscription} onClick={() => navigate("/billing")} />
        </div>
      </div>

      {/* ── Row 3: Overdue + Next due ── */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7">
          <SectionCard
            title="Next due"
            hint="Top items across hours, cycles, and calendar"
            testId="dashboard-next-due"
            count={nextDue.length}
            action={<Btn variant="ghost" onClick={() => navigate("/maintenance")} data-testid="next-due-see-all">See all</Btn>}
          >
            {nextDue.length === 0 ? (
              <EmptyState icon={CheckCircle} text="Nothing due in the next horizon. Nice." />
            ) : (
              <div className="space-y-1">
                {nextDue.map((it) => (
                  <ItemRow key={`${it.kind}-${it.id}`} item={it} onClick={() => goToAircraftItem(it)} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
        <div className="lg:col-span-5">
          <SectionCard
            title="Overdue"
            hint="Items past due — action needed"
            testId="dashboard-overdue"
            count={overdue.length}
          >
            {overdue.length === 0 ? (
              <EmptyState icon={CheckCircle} text="No overdue items." />
            ) : (
              <div className="space-y-1">
                {overdue.slice(0, 8).map((it) => (
                  <ItemRow key={`${it.kind}-${it.id}`} item={it} onClick={() => goToAircraftItem(it)} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── Row 4: Upcoming renewals + Parts due soon ── */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-6">
          <SectionCard
            title="Upcoming renewals"
            hint="ARC, transponder, ELT, insurance, registration — next 30 days"
            testId="dashboard-renewals"
            count={upcoming.length}
          >
            {upcoming.length === 0 ? (
              <EmptyState icon={CalendarBlank} text="No renewals in the next 30 days." />
            ) : (
              <div className="space-y-1">
                {upcoming.map((it) => (
                  <ItemRow key={`renewal-${it.id}`} item={it} onClick={() => goToAircraftItem(it)} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
        <div className="lg:col-span-6">
          <SectionCard
            title="Parts due soon"
            hint="Life-limit ≤ 30 days or ≤ 25 flight hours"
            testId="dashboard-parts-due"
            count={partsDue.length}
          >
            {partsDue.length === 0 ? (
              <EmptyState icon={Gear} text="No parts approaching life limit." />
            ) : (
              <div className="space-y-1">
                {partsDue.slice(0, 8).map((it) => (
                  <ItemRow key={`part-${it.id}`} item={it} onClick={() => goToAircraftItem(it)} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── Row 5: AD review + Missing documents ── */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-6">
          <SectionCard
            title="AD review"
            hint="ADs needing attention or scheduled review"
            testId="dashboard-ad-review"
            count={adReview.length}
            action={<Btn variant="ghost" onClick={() => navigate("/airworthiness")} data-testid="ad-review-see-all">See all</Btn>}
          >
            {adReview.length === 0 ? (
              <EmptyState icon={ShieldCheck} text="No ADs need attention." />
            ) : (
              <div className="space-y-1">
                {adReview.map((it) => (
                  <ItemRow key={`ad-${it.id}`} item={it} onClick={() => goToAircraftItem(it)} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
        <div className="lg:col-span-6">
          <SectionCard
            title="Missing documents"
            hint="Required documents not yet on file"
            testId="dashboard-missing-docs"
            count={missing.length}
            action={<Btn variant="ghost" onClick={() => navigate("/documents")} data-testid="missing-docs-see-all">Go to documents</Btn>}
          >
            {missing.length === 0 ? (
              <EmptyState icon={CheckCircle} text="Every required document is on file." />
            ) : (
              <div className="space-y-1">
                {missing.slice(0, 8).map((d, i) => (
                  <button
                    key={`miss-${i}`}
                    type="button"
                    onClick={() => navigate("/documents")}
                    className="w-full flex items-center justify-between px-3 py-2.5 border-l-2 border-amber/40 hover:bg-zinc-900/40 transition-colors text-left"
                    data-testid={`missing-doc-${d.aircraft_id}-${d.kind}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-100">{DOC_KIND_LABEL[d.kind] || d.kind}</div>
                      <div className="text-[10px] font-mono text-zinc-500">{d.aircraft_registration}</div>
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-amber">Missing</span>
                  </button>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── Row 6: Recent flights ── */}
      {flights.length > 0 && (
        <SectionCard
          title="Recent flights"
          hint="Last 5 flight logs"
          testId="dashboard-recent-flights"
          count={flights.length}
          action={<Btn variant="ghost" onClick={() => navigate("/logbook")} data-testid="flights-see-all">Open logbook</Btn>}
        >
          <div className="space-y-1">
            {flights.map((f) => (
              <div key={f.id} className="flex items-center justify-between px-3 py-2.5 border-l-2 border-zinc-800 hover:bg-zinc-900/30 transition-colors" data-testid={`flight-${f.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Lightning size={14} className="text-zinc-500" />
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{f.pic_name || "—"} · {f.aircraft_registration}</div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">{f.route || "—"}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono text-zinc-300">{f.duration_hours != null ? `${Number(f.duration_hours).toFixed(1)}h` : "—"}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{fmtDate(f.date)}</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider text-center pt-4">
        OwnerCAM does not replace your CAMO, A&amp;P/IA, or regulatory responsibility. Verify with qualified personnel and source documents.
      </div>
    </div>
  );
}
